import type { Cell } from '@tabel/shared/schemas/cells';
import type { Question } from '@tabel/shared/schemas/questions';
import type { Timesheet } from '@tabel/shared/schemas/timesheets';
import { CalendarDays, Clock, MessageCircleQuestion, PenLine, UserMinus, UserRound } from 'lucide-react';
import { useState } from 'react';
import { STATE_LABEL, STATE_SHORT, shownHours } from '../lib/format';
import { compareQuestions, dayLabel, isUnanswered, nq, plural, typeLabel } from '../lib/questions';
import { Badge, Button, ConfirmDialog, SidePanel } from './ui';

const days = (n: number) => `${n} ${plural(n, ['день', 'дня', 'дней'])}`;

/** Проблема дня — красным, как в табеле: «н/вых», «н/вх», «>24 ч», ошибка расчёта. */
const PROBLEM = new Set<Cell['machineState']>(['no_exit', 'no_entry', 'error', 'over_24h']);

/** Что стоит в ячейке — для подписи дня: код, часы (строкой сервера) или состояние. */
function cellText(c: Cell): { short: string; long: string; bad: boolean } {
  if (c.absenceCode) return { short: c.absenceCode, long: c.absenceCode, bad: false };
  if (c.finalHours != null) {
    const h = shownHours(c.finalValue, c.finalHours);
    return { short: h, long: `${h} ч`, bad: false };
  }
  const state = STATE_LABEL[c.machineState];
  if (!state) return { short: '', long: 'проходов нет', bad: false };
  return { short: STATE_SHORT[c.machineState], long: state, bad: PROBLEM.has(c.machineState) };
}

/** Состояние вопроса для строки списка. */
function stateBadge(q: Question) {
  if (q.state === 'clarify') return <Badge tone="warn">кадры просят уточнить</Badge>;
  if (q.state === 'later') return <Badge tone="neutral">отложен</Badge>;
  if (isUnanswered(q)) return <Badge tone="warn">без ответа</Badge>;
  if (q.state === 'accepted') return <Badge tone="ok">принят кадрами</Badge>;
  return <Badge tone="ok">отвечен</Badge>;
}

/**
 * Карточка сотрудника — режим правой панели (003 US3, сценарий 4; FR-034, FR-035; contracts/ui-panel.md, «Режимы
 * правой панели», «Окна»; прототип, Ж-04). Открывается нажатием на ФИО в табеле. Показывает часы за период (строкой
 * сервера — интерфейс часы не считает), дни на объекте, число правок, вопросы человека и дни периода: нажатие на день
 * открывает карточку дня, «Назад» в её шапке возвращает сюда. В подвале бригадира — «Убрать из табеля» с
 * подтверждением последствий (часы, дни, вопросы) и главная «Ответить на вопросы (N)».
 */
export function PersonCard({
  open,
  onClose,
  onBack,
  ts,
  personId,
  questions,
  canEdit,
  onOpenDay,
  onAnswer,
  onRemove,
  returnFocus,
}: {
  open: boolean;
  /** ✕ и Esc. Защиту несохранённого добавляет страница. */
  onClose: () => void;
  /** «Назад» в шапке: карточку открыли из другой панели. */
  onBack?: () => void;
  ts: Timesheet;
  personId: string;
  /** Вопросы табеля; без функции вопросов — нет. */
  questions?: readonly Question[];
  /** Бригадир своей бригады, «Черновик» и «Возвращён»: «Убрать из табеля», «Ответить на вопросы». */
  canEdit: boolean;
  /** Открыть карточку дня; `questionId` — с этим вопросом. */
  onOpenDay: (date: string, questionId: number | undefined) => void;
  /** «Ответить на вопросы (N)»: первый день человека с вопросом без ответа. */
  onAnswer?: () => void;
  /** Убрать человека после подтверждения. Ошибка — исключение: окно остаётся, тост с «Повторить» — у страницы. */
  onRemove: () => Promise<void>;
  returnFocus?: () => HTMLElement | null | undefined;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const row = ts.rows.find((r) => r.personId === personId);
  if (!row) return null;

  const past = row.cells.filter((c) => !ts.days.find((d) => d.date === c.date)?.future);
  const onSite = past.filter((c) => c.machineState !== 'empty').length;
  const withHours = row.cells.filter((c) => c.finalHours != null && c.finalHours > 0).length;
  const edits = row.cells.filter((c) => c.manualHours != null).length;
  const mine = (questions ?? []).filter((q) => q.personId === personId).sort(compareQuestions);
  const open_ = mine.filter(isUnanswered);
  const total = shownHours(row.totalValue, row.total) || '0';

  const remove = async () => {
    setBusy(true);
    try {
      await onRemove();
      setAsking(false);
    } catch {
      // Ошибку показывает страница (тост с «Повторить»); окно остаётся.
    } finally {
      setBusy(false);
    }
  };

  const fact = (icon: typeof Clock, label: string, value: string) => {
    const Icon = icon;
    return (
      <li className="flex items-baseline justify-between gap-3 border-b border-line py-2 last:border-b-0">
        <span className="flex items-center gap-2 text-ink-2">
          <Icon size={14} strokeWidth={1.75} className="shrink-0 self-center text-ink-3" aria-hidden />
          {label}
        </span>
        <b className="text-right font-medium text-ink tabular-nums">{value}</b>
      </li>
    );
  };

  const footer = (
    <>
      {canEdit && (
        <Button
          variant="ghost"
          icon={UserMinus}
          className="text-bad hover:text-bad"
          onClick={() => setAsking(true)}
        >
          Убрать из табеля
        </Button>
      )}
      <span className="flex-1" aria-hidden />
      {canEdit && onAnswer && open_.length > 0 ? (
        <Button variant="primary" icon={MessageCircleQuestion} onClick={onAnswer}>
          Ответить на вопросы
          <span className="rounded-full bg-white/20 px-1.5 text-[12px] font-semibold tabular-nums">
            {open_.length}
          </span>
        </Button>
      ) : (
        <Button onClick={onClose}>Закрыть</Button>
      )}
    </>
  );

  const removeText = [
    `${row.displayName}: из итога уйдут ${total} ч${withHours > 0 ? ` за ${days(withHours)}` : ''}.`,
    questions && mine.length > 0
      ? `Снимутся вопросы: ${mine.length}${open_.length > 0 ? `, из них без ответа — ${open_.length}` : ''}.`
      : null,
    'Ответы, правки и история останутся в журнале: если вернуть человека, всё вернётся.',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        onBack={onBack}
        label="Карточка сотрудника"
        crumb={ts.brigadeName}
        title="Сотрудник"
        icon={UserRound}
        footer={footer}
        returnFocus={returnFocus}
      >
        <div>
          <h2 className="text-[18px] font-semibold text-ink">{row.displayName}</h2>
          <p className="mt-0.5 text-[13px] text-ink-2 tabular-nums">
            № СКУД {row.personId} · бригада «{ts.brigadeName}»
          </p>
        </div>

        <ul aria-label="Сведения" className="text-[13px]">
          {fact(Clock, 'Часов за период', `${total} ч`)}
          {fact(CalendarDays, 'На объекте', `${days(onSite)} из ${past.length} прошедших`)}
          {fact(PenLine, 'Правок', String(edits))}
          {questions &&
            fact(
              MessageCircleQuestion,
              'Вопросы',
              mine.length === 0
                ? 'нет'
                : `${nq(mine.length)} · ${open_.length > 0 ? `без ответа ${open_.length}` : 'все отвечены'}`,
            )}
        </ul>

        <section>
          <h3 className="mb-2 text-[14px] font-semibold text-ink">Дни периода</h3>
          <ul
            aria-label="Дни периода"
            data-days
            className="grid grid-cols-7 gap-1 max-sm:grid-cols-5 min-[420px]:max-sm:grid-cols-6"
          >
            {row.cells.map((c) => {
              const day = ts.days.find((d) => d.date === c.date);
              const t = cellText(c);
              const mark = c.question ?? null;
              const base = `relative flex min-h-10 flex-col items-center justify-center rounded-control border text-[12px] leading-tight tabular-nums ${day?.weekend ? 'bg-weekend' : 'bg-surface'} ${c.manualHours != null ? 'bg-edit' : ''}`;
              const label = [
                `${dayLabel(c.date)}: ${t.long}`,
                c.manualHours != null ? 'изменено вручную' : null,
                mark === 'open' ? 'вопрос без ответа' : mark === 'done' ? 'ответ дан' : null,
              ]
                .filter(Boolean)
                .join(', ');
              const inner = (
                <>
                  <span className="text-[11px] text-ink-3">{Number(c.date.slice(8, 10))}</span>
                  <span className={`min-h-4 font-medium ${t.bad ? 'text-bad' : 'text-ink'}`}>{t.short}</span>
                  {mark === 'open' && (
                    <span
                      aria-hidden
                      className="absolute top-0 right-0 h-0 w-0 border-t-[7px] border-l-[7px] border-t-warn border-l-transparent"
                    />
                  )}
                  {mark === 'done' && (
                    <span
                      aria-hidden
                      className="absolute right-[3px] bottom-[1px] text-[9px] font-bold text-ok"
                    >
                      ✓
                    </span>
                  )}
                </>
              );
              return (
                <li key={c.date}>
                  {day?.future ? (
                    <span className={`${base} border-line opacity-50`} title={dayLabel(c.date)}>
                      <span className="text-[11px] text-ink-3">{Number(c.date.slice(8, 10))}</span>
                      <span className="min-h-4" />
                    </span>
                  ) : (
                    <button
                      type="button"
                      aria-label={label}
                      title={label}
                      onClick={() => onOpenDay(c.date, undefined)}
                      className={`${base} w-full border-line transition-colors hover:border-accent hover:bg-accent-soft`}
                    >
                      {inner}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {mine.length > 0 && (
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-[14px] font-semibold text-ink">
              Вопросы
              <span className="text-[12px] font-normal text-ink-3 tabular-nums">{mine.length}</span>
            </h3>
            <ul aria-label="Вопросы сотрудника" className="flex flex-col gap-1.5">
              {mine.map((q) => (
                <li key={q.id}>
                  <button
                    type="button"
                    onClick={() => onOpenDay(q.date, q.id)}
                    className="flex min-h-10 w-full flex-wrap items-center gap-x-2 gap-y-1 rounded-control border border-line px-3 py-2 text-left text-[13px] transition-colors hover:border-accent hover:bg-accent-soft"
                  >
                    <b className="font-semibold text-ink">{dayLabel(q.date)}</b>
                    <span className="min-w-0 flex-1 text-ink-2">{typeLabel(q.type)}</span>
                    {stateBadge(q)}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </SidePanel>

      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        onConfirm={() => void remove()}
        busy={busy}
        icon={UserMinus}
        title={`Убрать из табеля «${ts.brigadeName}»?`}
        description={removeText}
        confirmLabel="Убрать из табеля"
      />
    </>
  );
}
